// Beginner games, part 2: the risk math every trade needs. Most have no
// chart: they make up a realistic scenario (coin, account, entry, stop) with
// `rng` and grade against the desk's own numbers in rules.ts.
import type { Fact, GameDef, Option, Rng } from "../types";
import { atr, chartOf, clockText, dateText, numberOptions, pctText, planOutcome, price, rOf, rText, usd } from "../helpers";
import {
  costR, MAX_COST_R, MAX_DAILY_LOSS_R, MAX_TRADES_PER_DAY, MIN_RR, RISK_PCT, roundTripCostPct, SESSIONS,
} from "../rules";

type Side = "LONG" | "SHORT";

// ── Scenario building blocks ────────────────────────────────────────────────

interface Coin {
  name: string;
  /** Realistic price range. */
  lo: number;
  hi: number;
  /** Price step: prices are whole ticks, so the shown price is the exact price. */
  tick: number;
  /** Decimals a position size in this coin is usually written with. */
  size: number;
}

const COINS: readonly Coin[] = [
  { name: "BTC", lo: 60000, hi: 70000, tick: 0.1, size: 4 },
  { name: "ETH", lo: 3000, hi: 3500, tick: 0.01, size: 3 },
  { name: "SOL", lo: 130, hi: 160, tick: 0.01, size: 2 },
  { name: "BNB", lo: 550, hi: 600, tick: 0.01, size: 2 },
  { name: "XRP", lo: 0.5, hi: 0.6, tick: 0.0001, size: 0 },
  { name: "DOGE", lo: 0.12, hi: 0.2, tick: 0.0001, size: 0 },
  { name: "LINK", lo: 12, hi: 16, tick: 0.01, size: 1 },
  { name: "AVAX", lo: 25, hi: 35, tick: 0.01, size: 2 },
  { name: "ADA", lo: 0.35, hi: 0.45, tick: 0.0001, size: 0 },
];

const tickDecimals = (tick: number) => Math.max(0, Math.round(-Math.log10(tick)));
/** A whole number of ticks as a clean price (no float dust). */
const atTicks = (coin: Coin, ticks: number) => Number((ticks * coin.tick).toFixed(tickDecimals(coin.tick)));

interface Plan {
  coin: Coin;
  side: Side;
  /** Entry, stop and the distance between them, in ticks. */
  e: number;
  s: number;
  d: number;
  entry: number;
  stop: number;
  /** Entry-to-stop distance in price (per coin). */
  risk: number;
}

/** A made-up entry and stop: stop `stopPct` % away (at least `minTicks` ticks), below the entry for a buy, above for a sell. */
function makePlan(rng: Rng, opts: { stopPct: [number, number]; minTicks: number; even?: boolean }): Plan {
  const coin = rng.pick(COINS);
  const side: Side = rng.chance(0.5) ? "LONG" : "SHORT";
  const e = rng.int(Math.round(coin.lo / coin.tick), Math.round(coin.hi / coin.tick));
  let d = Math.max(opts.minTicks, Math.round((e * rng.float(opts.stopPct[0], opts.stopPct[1])) / 100));
  if (opts.even && d % 2 === 1) d++;
  const s = side === "LONG" ? e - d : e + d;
  return { coin, side, e, s, d, entry: atTicks(coin, e), stop: atTicks(coin, s), risk: atTicks(coin, d) };
}

const ROUND_ACCOUNTS = [200, 250, 300, 500, 750, 1000, 1500, 2000, 2500, 3000, 5000, 7500, 10000] as const;
/** $200–$10,000: a round number or any multiple of $50. */
const pickAccount = (rng: Rng): number => (rng.chance(0.5) ? rng.pick(ROUND_ACCOUNTS) : rng.int(4, 200) * 50);
/** Risk per trade, %: the desk's 0.5% most often. */
const RISKS = [0.25, 0.5, 0.5, 0.5, 0.75, 1, 1, 1.5, 2] as const;

/** A per-coin price distance as dollars: "$2.10", "$0.0023", "$650.30". */
const dollars = (n: number): string => `${n < 0 ? "−" : ""}$${price(Math.abs(n))}`;
/** A coin amount: "1,234", "2.38", "0.0077". */
const qty = (n: number, dec: number): string => n.toLocaleString("en-US", { minimumFractionDigits: dec, maximumFractionDigits: dec });
const sideFact = (side: Side) => (side === "LONG" ? "Buy" : "Sell");
const verb = (side: Side) => (side === "LONG" ? "buy" : "sell");
const past = (side: Side) => (side === "LONG" ? "bought" : "sold");
/** "2", "1.5", "2.03": a number with at most 2 decimals and no trailing zeros. */
const short = (n: number): string => String(Number(n.toFixed(2)));
/** "+1.5 − 1.0 + 0.5": a list of R values written as one sum. */
function sumText(rs: number[]): string {
  return rs.map((r, i) => `${i === 0 ? (r < 0 ? "−" : "+") : r < 0 ? " − " : " + "}${Math.abs(r).toFixed(1)}`).join("");
}

// ── Pure rules (exported for the tests) ─────────────────────────────────────

/** Dollars at risk: account × risk % ÷ 100. */
export const riskUsdOf = (account: number, riskPct: number): number => (account * riskPct) / 100;

/** Coins to trade so the stop loses exactly the risk: risk $ ÷ |entry − stop|. */
export const positionSizeOf = (riskUsd: number, entry: number, stop: number): number => riskUsd / Math.abs(entry - stop);

/** The stop's distance as % of the entry. */
export const stopPctOf = (entry: number, stop: number): number => (Math.abs(entry - stop) / entry) * 100;

/** Reward ÷ risk: how many times the risk the target pays. */
export const rewardToRiskOf = (entry: number, stop: number, target: number): number => Math.abs(target - entry) / Math.abs(entry - stop);

/** What a plan is: a buy (stop below, target above), a sell (the mirror), or broken (both on one side). */
export function planKindOf(entry: number, stop: number, target: number): "buy" | "sell" | "broken" {
  if (stop < entry && target > entry) return "buy";
  if (stop > entry && target < entry) return "sell";
  return "broken";
}

/** The win rate (%) at which wins of `winR` and losses of 1R cancel out: 1 ÷ (1 + W). */
export const breakevenPctOf = (winR: number): number => 100 / (1 + winR);

/** May you take another trade today? The loss limit is checked first, like the backend's daily risk guard. */
export function dayVerdictOf(rs: number[]): "yes" | "trades" | "loss" {
  const sum = rs.reduce((a, b) => a + b, 0);
  if (sum <= -MAX_DAILY_LOSS_R + 1e-9) return "loss";
  if (rs.length >= MAX_TRADES_PER_DAY) return "trades";
  return "yes";
}

/** The session a UTC hour is in. A session owns its first hour (07:00 is London). */
export function sessionOfHour(hour: number): (typeof SESSIONS)[number] {
  return SESSIONS.find(s => hour >= s.from && hour < s.to) ?? SESSIONS[SESSIONS.length - 1]!;
}

// ── 11. Risk in dollars ─────────────────────────────────────────────────────

export const riskDollars: GameDef = {
  id: "risk-dollars",
  level: "beginner",
  title: "Risk in dollars",
  skill: "Turn your risk % into the dollars one trade may lose.",
  make(_data, rng) {
    const account = pickAccount(rng);
    const riskPct = rng.pick(RISKS);
    const answer = riskUsdOf(account, riskPct);
    // Sometimes the account has grown or shrunk since the month began: risk is a % of it as it is now.
    const moved = rng.chance(0.3);
    let start = 0;
    const facts: Fact[] = [];
    if (moved) {
      start = Math.round((account * (1 + (rng.chance(0.5) ? 1 : -1) * rng.float(0.08, 0.25))) / 10) * 10;
      facts.push({ label: "Start of month", value: usd(start, 0) }, { label: "Account now", value: usd(account, 0) });
    } else {
      facts.push({ label: "Account", value: usd(account, 0) });
    }
    facts.push({ label: "Risk per trade", value: `${riskPct}%` });
    const streak = Math.round((1 - (1 - riskPct / 100) ** 10) * 100);
    const why =
      riskPct === RISK_PCT
        ? `${RISK_PCT}% is the desk's default: even 10 losses in a row cost only about ${streak}% of the account.`
        : `At ${riskPct}%, 10 losses in a row cost about ${streak}% of the account (the desk's default of ${RISK_PCT}% keeps that near 5%).`;
    return {
      kind: "number",
      prompt: moved
        ? "Your next trade risks the % below. How many dollars is that?"
        : `You risk ${riskPct}% of your account on each trade. How many dollars do you lose if this trade hits its stop?`,
      facts,
      answer,
      tolerance: 0.01,
      unit: "$",
      decimals: 2,
      explain:
        `Risk = account × risk % ÷ 100 = ${usd(account, 0)} × ${riskPct} ÷ 100 = ${usd(answer)}: the most this trade may lose.` +
        (moved ? ` Use the account as it is now, not the ${usd(start, 0)} it started the month with.` : ` ${why}`),
    };
  },
};

// ── 12. Size the position ───────────────────────────────────────────────────

export const positionSize: GameDef = {
  id: "position-size",
  level: "beginner",
  title: "Size the position",
  skill: "Turn your risk in dollars and your stop into how many coins to buy or sell.",
  make(_data, rng) {
    const account = pickAccount(rng);
    const riskPct = rng.pick(RISKS);
    const p = makePlan(rng, { stopPct: [0.5, 3], minTicks: 10 });
    const showTarget = rng.chance(0.4);
    const rr = rng.pick([1.5, 2, 2.5]);
    const riskUsd = riskUsdOf(account, riskPct);
    const coins = positionSizeOf(riskUsd, p.entry, p.stop);
    // Enough decimals that the shown size is within 1% of the exact one.
    let dec = p.coin.size;
    while (coins * 10 ** dec < 50 && dec < 8) dec++;
    const facts: Fact[] = [
      { label: "Account", value: usd(account, 0) },
      { label: "Risk per trade", value: `${riskPct}%` },
      { label: "Side", value: sideFact(p.side) },
      { label: "Entry", value: price(p.entry) },
      { label: "Stop", value: price(p.stop) },
    ];
    if (showTarget) {
      const t = p.side === "LONG" ? p.e + Math.round(rr * p.d) : p.e - Math.round(rr * p.d);
      facts.push({ label: "Target", value: price(atTicks(p.coin, t)) });
    }
    const note = showTarget
      ? "The target doesn't change the size: only the stop does."
      : p.side === "SHORT"
        ? "For a sell the stop sits above the entry, but the math is the same: the distance between them."
        : `That's a ${usd(coins * p.entry, 0)} position, but only ${usd(riskUsd)} of it is at risk.`;
    return {
      kind: "number",
      prompt: `How many ${p.coin.name} do you ${verb(p.side)} so that hitting the stop loses exactly your risk?`,
      facts,
      answer: coins,
      tolerance: coins * 0.02,
      unit: "coins",
      decimals: dec,
      explain:
        `Risk ${usd(riskUsd)} (${riskPct}% of ${usd(account, 0)}) ÷ ${dollars(p.risk)} lost per coin from entry to stop = ${qty(coins, dec)} ${p.coin.name}. ${note}`,
    };
  },
};

// ── 13. How far is the stop? ────────────────────────────────────────────────

export const stopDistance: GameDef = {
  id: "stop-distance",
  level: "beginner",
  title: "How far is the stop?",
  skill: "Measure a stop as a % of the entry, and see why very tight stops cost too much in fees.",
  make(_data, rng) {
    // Some stops tighter than the fee limit (about 0.63%), most comfortably wider.
    const tight = rng.chance(0.3);
    const p = makePlan(rng, { stopPct: tight ? [0.25, 0.55] : [0.75, 3.5], minTicks: 5 });
    const answer = stopPctOf(p.entry, p.stop);
    const cr = costR(p.entry, p.stop);
    const rt = roundTripCostPct(6);
    const [far, near] = p.side === "LONG" ? [p.entry, p.stop] : [p.stop, p.entry];
    const costLine =
      cr > MAX_COST_R
        ? `that's over the desk's ${MAX_COST_R}R limit, so the engine skips a stop this tight.`
        : `that's under the desk's ${MAX_COST_R}R limit. Below about ${pctText(rt / MAX_COST_R)} the fees would eat too much of the risk.`;
    return {
      kind: "number",
      prompt: `You ${verb(p.side)} ${p.coin.name} with the stop shown. How far away is the stop, as a % of the entry?`,
      facts: [
        { label: "Side", value: sideFact(p.side) },
        { label: "Entry", value: price(p.entry) },
        { label: "Stop", value: price(p.stop) },
      ],
      answer,
      tolerance: 0.03,
      unit: "%",
      decimals: 2,
      explain:
        `(${price(far)} − ${price(near)}) ÷ ${price(p.entry)} × 100 = ${pctText(answer)}. ` +
        `Fees, slippage and funding cost about ${pctText(rt)} per trade, which eats ${cr.toFixed(2)}R of this stop's risk: ${costLine}`,
    };
  },
};

// ── 14. Reward vs risk ──────────────────────────────────────────────────────

export const rewardToRisk: GameDef = {
  id: "reward-to-risk",
  level: "beginner",
  title: "Reward vs risk",
  skill: "Work out how many times the risk a target pays, and check it against the desk's 1.5× minimum.",
  make(_data, rng) {
    const rr = rng.pick([0.8, 1, 1.2, 1.3, 1.5, 1.5, 1.8, 2, 2, 2.5, 3, 3.5]);
    // An even stop distance makes a 1.5× target exactly 1.5×.
    const p = makePlan(rng, { stopPct: [0.6, 3], minTicks: 20, even: true });
    const dir = p.side === "LONG" ? 1 : -1;
    const t = p.e + dir * Math.round(rr * p.d);
    const target = atTicks(p.coin, t);
    const answer = Math.abs(t - p.e) / p.d;
    // A ratio just either side of 1.5 is a close call: skip it (1.5 itself is exact and passes).
    if (answer !== MIN_RR && Math.abs(answer - MIN_RR) < 0.15) return null;
    const reward = atTicks(p.coin, Math.abs(t - p.e));
    const verdict =
      answer === MIN_RR
        ? `That's exactly the desk's minimum of ${MIN_RR}×, so the plan just passes.`
        : answer > MIN_RR
          ? `The desk needs at least ${MIN_RR}×, so this plan passes.`
          : `That's under the desk's minimum of ${MIN_RR}×, so the engine would skip it: wins this small can't pay for the losses.`;
    return {
      kind: "number",
      prompt: `How many times the risk does this ${p.coin.name} ${verb(p.side)} plan's target pay?`,
      facts: [
        { label: "Side", value: sideFact(p.side) },
        { label: "Entry", value: price(p.entry) },
        { label: "Stop", value: price(p.stop) },
        { label: "Target", value: price(target) },
      ],
      answer,
      tolerance: 0.05,
      unit: "×",
      decimals: 1,
      explain: `Reward ${dollars(reward)} (entry to target) ÷ risk ${dollars(p.risk)} (entry to stop) = ${short(answer)}×. ${verdict}`,
    };
  },
};

// ── 15. Buy or sell plan? ───────────────────────────────────────────────────

const PLAN_OPTIONS = {
  buy: "A buy: profit if price rises",
  sell: "A sell: profit if price falls",
  broken: "A broken plan: stop and target are on the same side",
} as const;

export const buyOrSellPlan: GameDef = {
  id: "buy-or-sell-plan",
  level: "beginner",
  title: "Buy or sell plan?",
  skill: "Read a plan's direction from where its stop and target sit around the entry.",
  needs: { tf: "1h", history: 80, ahead: 24 },
  make(data, rng) {
    if (!data) return null;
    const c = data.candles;
    const a = atr(c);
    if (a === null || a <= 0) return null;
    const entry = c[c.length - 1]!.close;
    const hi = Math.max(...c.map(k => k.high));
    const lo = Math.min(...c.map(k => k.low));
    // Lines must sit inside the candles' range, or the chart won't show them.
    const roomUp = 0.9 * (hi - entry);
    const roomDown = 0.9 * (entry - lo);
    const kind: "buy" | "sell" | "broken" = rng.chance(0.2) ? "broken" : rng.chance(0.5) ? "buy" : "sell";
    let stop: number;
    let target: number;
    if (kind === "broken") {
      const up = rng.chance(0.5);
      const room = up ? roomUp : roomDown;
      let near = a * rng.float(0.8, 1.4);
      let far = near * rng.float(1.8, 3);
      if (far > room) {
        near *= room / far;
        far = room;
      }
      if (near < 0.5 * a) return null;
      const dir = up ? 1 : -1;
      const stopNear = rng.chance(0.5);
      stop = entry + dir * (stopNear ? near : far);
      target = entry + dir * (stopNear ? far : near);
    } else {
      const rr = rng.pick([1.5, 2, 2.5, 3]);
      const [stopRoom, targetRoom] = kind === "buy" ? [roomDown, roomUp] : [roomUp, roomDown];
      const risk = Math.min(a * rng.float(0.8, 1.6), stopRoom, targetRoom / rr);
      if (risk < 0.5 * a) return null;
      const dir = kind === "buy" ? 1 : -1;
      stop = entry - dir * risk;
      target = entry + dir * rr * risk;
    }
    const answer = planKindOf(entry, stop, target);
    const where = (p: number) => (p > entry ? "above" : "below");
    const options: Option[] = (["buy", "sell", "broken"] as const).map(id => ({
      id,
      label: PLAN_OPTIONS[id],
      why:
        id === answer
          ? undefined
          : id === "broken"
            ? `The stop is ${where(stop)} the entry and the target ${where(target)} it: opposite sides, so the plan works.`
            : answer === "broken"
              ? `Both lines are ${where(stop)} the entry. A ${id} needs its stop ${id === "buy" ? "below" : "above"} and its target ${id === "buy" ? "above" : "below"}.`
              : `The target is ${where(target)} the entry, so this plan makes money only if price ${target > entry ? "rises" : "falls"}.`,
    }));
    const explain =
      answer === "broken"
        ? `Both the stop (${price(stop)}) and the target (${price(target)}) are ${where(stop)} the entry (${price(entry)}). A plan needs them on opposite sides: the stop where you're wrong, the target where you're right.`
        : `The target (${price(target)}) is ${where(target)} the entry (${price(entry)}) and the stop (${price(stop)}) is ${where(stop)} it, so price ${answer === "buy" ? "rising" : "falling"} pays: it's a ${answer}. The stop always sits on the losing side, the target on the winning side.`;
    const lastAhead = data.ahead[data.ahead.length - 1];
    const outcome =
      answer === "broken"
        ? lastAhead
          ? `Over the next ${data.ahead.length} candles price went from ${price(entry)} to ${price(lastAhead.close)}.`
          : undefined
        : planOutcome(answer === "buy" ? "LONG" : "SHORT", entry, stop, target, data.ahead) || undefined;
    return {
      kind: "choice",
      prompt: `The lines show a ${data.coin} plan: Entry at the last close, a Stop and a Target. What kind of plan is it?`,
      chart: chartOf(data, {
        focus: 60,
        lines: [
          { price: entry, label: "Entry", tone: "brand" },
          { price: stop, label: "Stop", tone: "loss" },
          { price: target, label: "Target", tone: "profit" },
        ],
      }),
      options,
      answer,
      explain,
      outcome,
    };
  },
};

// ── 16. Result in R ─────────────────────────────────────────────────────────

export const resultInR: GameDef = {
  id: "result-in-r",
  level: "beginner",
  title: "Result in R",
  skill: "Measure a closed trade in R: the profit or loss as a multiple of the risk you planned.",
  make(_data, rng) {
    const p = makePlan(rng, { stopPct: [0.6, 3], minTicks: 20 });
    const type = rng.pick(["win", "win", "loss", "loss", "partial", "partial"] as const);
    const r0 =
      type === "win" ? rng.pick([1.5, 2, 2.5, 3]) : type === "loss" ? rng.pick([-1, -1, -1, -1.1, -1.2]) : rng.pick([-0.7, -0.4, 0, 0.3, 0.6, 0.9, 1.2]);
    const dir = p.side === "LONG" ? 1 : -1;
    const exit = atTicks(p.coin, p.e + dir * Math.round(r0 * p.d));
    const answer = rOf(p.side, p.entry, p.stop, exit);
    const shown = Number(answer.toFixed(2));
    const formula =
      p.side === "LONG"
        ? `R = (exit − entry) ÷ risk = (${price(exit)} − ${price(p.entry)}) ÷ ${price(p.risk)} = ${rText(shown, 2)}.`
        : `For a sell flip the sign, because a sell makes money when price falls: R = (entry − exit) ÷ risk = (${price(p.entry)} − ${price(exit)}) ÷ ${price(p.risk)} = ${rText(shown, 2)}.`;
    const tail =
      shown === 0
        ? "Closing at the entry is 0R: no gain, no loss."
        : shown < -1.005
          ? "Worse than −1R: price jumped past the stop before the order filled (slippage)."
          : shown <= -0.995
            ? "A loss at the stop is exactly −1R: the loss you planned."
            : type === "win"
              ? `You made ${short(shown)} times what you risked.`
              : "Closing before the stop or the target gives a part-R result like this.";
    return {
      kind: "number",
      prompt: `You ${past(p.side)} ${p.coin.name} and closed the trade at the exit price. What was the result in R?`,
      facts: [
        { label: "Side", value: sideFact(p.side) },
        { label: "Entry", value: price(p.entry) },
        { label: "Stop", value: price(p.stop) },
        { label: "Exit", value: price(exit) },
      ],
      answer,
      tolerance: 0.05,
      unit: "R",
      decimals: 2,
      explain: `R (one unit of the risk you planned) is the distance from entry to stop: ${dollars(p.risk)} per coin here. ${formula} ${tail}`,
    };
  },
};

// ── 17. Win rate to break even ──────────────────────────────────────────────

export const breakevenWinRate: GameDef = {
  id: "breakeven-win-rate",
  level: "beginner",
  title: "Win rate to break even",
  skill: "See how bigger wins lower the win rate you need to break even.",
  make(_data, rng) {
    const w = rng.pick([1, 1.5, 2, 2.5, 3]);
    const be = breakevenPctOf(w);
    const wText = rText(w, w % 1 ? 1 : 0);
    const label = (v: number) => `${Math.round(v)}%`;
    const made = numberOptions(be, rng, label, { step: 10, min: 5 });
    const options: Option[] = made.options.map(o => {
      if (o.id === made.answer) return o;
      const p = Number(o.label.replace("%", ""));
      const total = p * w - (100 - p);
      return {
        ...o,
        why: `At ${p}%, 100 trades make ${p} × ${short(w)}R − ${100 - p} × 1R = ${rText(total, total % 1 ? 1 : 0)}: ${total > 0 ? "a profit, so that's above break-even" : "a loss, so that's below break-even"}.`,
      };
    });
    // The 10-trade check: exact when 10 trades split evenly, else the wins either side of break-even.
    const k = 10 / (1 + w);
    const tenTrades = Number.isInteger(k)
      ? `In 10 trades, ${k} wins (+${short(k * w)}R) against ${10 - k} losses (−${10 - k}R) come out exactly even.`
      : (() => {
          const lo = Math.floor(k);
          const hi = lo + 1;
          return `In 10 trades, ${lo} wins (+${short(lo * w)}R) against ${10 - lo} losses (−${10 - lo}R) lose ${short(10 - lo - lo * w)}R, while ${hi} wins (+${short(hi * w)}R) against ${10 - hi} losses make ${short(hi * w - (10 - hi))}R, so break-even sits in between.`;
        })();
    return {
      kind: "choice",
      prompt: `Your wins pay ${short(w)}× the risk (${wText}) and your losses cost 1× (−1R). What win rate do you need just to break even?`,
      facts: [
        { label: "Each win", value: wText },
        { label: "Each loss", value: rText(-1, 0) },
      ],
      options,
      answer: made.answer,
      explain: `Break-even win rate = 1 ÷ (1 + ${short(w)}) = ${short(be)}%. ${tenTrades} The bigger your wins, the less often you need to be right.`,
    };
  },
};

// ── 18. R to dollars ────────────────────────────────────────────────────────

export const rToDollars: GameDef = {
  id: "r-to-dollars",
  level: "beginner",
  title: "R to dollars",
  skill: "Add up a day's results in R and turn them into dollars.",
  make(_data, rng) {
    const riskUsd = rng.pick([2.5, 5, 5, 7.5, 10, 10, 12.5, 15, 20, 25, 50]);
    const n = rng.int(3, 5);
    // Tenths of R, so the sums are exact.
    const tenths = Array.from({ length: n }, () => rng.pick([-10, -10, -10, -5, 0, 5, 10, 15, 15, 20, 25, 30]));
    const sumT = tenths.reduce((a, b) => a + b, 0);
    const rs = tenths.map(t => t / 10);
    const sumR = sumT / 10;
    const answer = (sumT * riskUsd) / 10;
    return {
      kind: "number",
      prompt: `You risk ${usd(riskUsd)} on every trade. How many dollars did today's trades make or lose in total? (Type a minus sign for a loss.)`,
      facts: [{ label: "Risk per trade", value: usd(riskUsd) }, ...rs.map((r, i) => ({ label: `Trade ${i + 1}`, value: rText(r, 1) }))],
      answer,
      tolerance: 0.01,
      unit: "$",
      decimals: 2,
      explain:
        `Add the R first: ${sumText(rs)} = ${rText(sumR, 1)}. Then ${rText(sumR, 1)} × ${usd(riskUsd)} per R = ${usd(answer)}. ` +
        "Counting in R keeps your results comparable at any account size.",
    };
  },
};

// ── 19. One more trade? ─────────────────────────────────────────────────────

/** Today's R values (tenths) for a day that lands on `kind`, never near the other limit. */
function dayScenario(rng: Rng, kind: "yes" | "trades" | "loss"): number[] {
  // A day at −2R or better leaves room for one more 1R loss before −3R.
  const pool = [-20, -15, -12, -10, -10, -10, -5, 0, 5, 10, 15, 15, 20, 25];
  const draw = (n: number) => {
    for (let i = 0; i < 40; i++) {
      const t = Array.from({ length: n }, () => rng.pick(pool));
      if (t.reduce((a, b) => a + b, 0) >= -20) return t;
    }
    return Array.from({ length: n }, (_, i) => (i % 2 === 0 ? 10 : -10));
  };
  if (kind === "yes") return draw(rng.pick([0, 1, 1, 2, 2]));
  if (kind === "trades") return draw(rng.pick([3, 3, 3, 3, 4]));
  // Loss: 1–2 trades that reach −3R (so the trade limit is not hit too).
  if (rng.chance(0.2)) return [rng.pick([-30, -32])];
  const first = rng.pick([-10, -10, -12, -15, -20]);
  const second = -30 - first - rng.pick([0, 0, 2, 5]);
  return rng.shuffle([first, second]);
}

const DAY_OPTIONS = {
  yes: "Yes, you can take another",
  trades: `No: ${MAX_TRADES_PER_DAY} trades already today`,
  loss: `No: the day has lost ${MAX_DAILY_LOSS_R}R`,
} as const;

export const dayLimit: GameDef = {
  id: "day-limit",
  level: "beginner",
  title: "One more trade?",
  skill: "Apply the desk's daily stop: at most 3 trades a day, and no more once the day is down 3R.",
  make(_data, rng) {
    const kind = rng.pick(["yes", "yes", "trades", "trades", "loss"] as const);
    const rs = dayScenario(rng, kind).map(t => t / 10);
    const n = rs.length;
    const sum = Math.round(rs.reduce((a, b) => a + b, 0) * 10) / 10;
    const answer = dayVerdictOf(rs);
    const trades = `${n} trade${n === 1 ? "" : "s"}`;
    const options: Option[] = (["yes", "trades", "loss"] as const).map(id => ({
      id,
      label: DAY_OPTIONS[id],
      why:
        id === answer
          ? undefined
          : id === "yes"
            ? answer === "trades"
              ? `You've already taken ${trades} today: the day stops at ${MAX_TRADES_PER_DAY}.`
              : `Today's trades add up to ${rText(sum, 1)}: the day is over at −${MAX_DAILY_LOSS_R}R.`
            : id === "trades"
              ? `${n === 0 ? "No trades" : `Only ${trades}`} so far: the limit is ${MAX_TRADES_PER_DAY}.`
              : `Today adds up to ${rText(sum, 1)}: the day stops only once it reaches −${MAX_DAILY_LOSS_R}R.`,
    }));
    const today =
      n === 0
        ? "No trades yet today, so you can take it."
        : `Today: ${trades}, ${sumText(rs)} = ${rText(sum, 1)}, so ${
            answer === "yes"
              ? "neither limit is hit and you can take it"
              : answer === "trades"
                ? n === MAX_TRADES_PER_DAY
                  ? `that's the ${MAX_TRADES_PER_DAY}-trade limit: no more today`
                  : `that's already past the ${MAX_TRADES_PER_DAY}-trade limit: stop now`
                : `the day has reached −${MAX_DAILY_LOSS_R}R: stop until tomorrow`
          }.`;
    const protect =
      answer === "loss"
        ? `At the desk's ${RISK_PCT}% risk, −${MAX_DAILY_LOSS_R}R is only ${RISK_PCT * MAX_DAILY_LOSS_R}% of the account: stopping keeps a bad day small and stops "revenge trades" to win it back.`
        : answer === "trades"
          ? "The engine finds about 3 good signals a week, so a 4th trade in one day usually means you went looking for trades."
          : `Just remember: one more loss would leave the day at ${rText(sum - 1, 1)}, so take only a real signal at your normal risk.`;
    const facts: Fact[] = [{ label: "Trades closed today", value: String(n) }, ...rs.map((r, i) => ({ label: `Trade ${i + 1}`, value: rText(r, 1) }))];
    return {
      kind: "choice",
      prompt: "A new signal just came in. Given today's closed trades, can you take it?",
      facts,
      options,
      answer,
      explain: `The desk stops the day after ${MAX_TRADES_PER_DAY} trades, or once the closed trades add up to −${MAX_DAILY_LOSS_R}R. ${today} ${protect}`,
    };
  },
};

// ── 20. Which session? ──────────────────────────────────────────────────────

const SESSION_NOTES: Record<(typeof SESSIONS)[number]["id"], string> = {
  asia: "Asia is usually the quietest stretch: smaller moves, and ranges that London often breaks later.",
  london: "London and New York are the busiest hours; that's when most moves start.",
  "new-york": "New York and London are the busiest hours; US news lands here, so moves can be fast.",
  late: "Volume drops as the US day ends, so moves in the late session are usually smaller.",
};

const hh = (h: number) => `${String(h).padStart(2, "0")}:00`;

export const sessionName: GameDef = {
  id: "session-name",
  level: "beginner",
  title: "Which session?",
  skill: "Know which trading session (Asia, London, New York, late) a UTC time falls in.",
  make(_data, rng) {
    const fifteen = rng.chance(0.3);
    const day = 1_767_225_600 + rng.int(0, 280) * 86400; // a day in 2026
    // Session starts come up often: they're the hours people get wrong.
    const hour = rng.chance(0.35) ? rng.pick(SESSIONS.map(s => s.from)) : rng.int(0, 23);
    const minute = fifteen ? rng.pick([0, 15, 30, 45]) : 0;
    const t = day + hour * 3600 + minute * 60;
    const s = sessionOfHour(hour);
    const idx = SESSIONS.findIndex(x => x.id === s.id);
    const prev = SESSIONS[(idx + SESSIONS.length - 1) % SESSIONS.length]!;
    const boundary =
      minute === 0 && hour === s.from
        ? hour === 0
          ? " 00:00 starts a new day, and Asia with it."
          : ` A session owns its first hour: ${clockText(t)} already counts as ${s.label}, not ${prev.label}.`
        : "";
    return {
      kind: "choice",
      prompt: `A ${fifteen ? "15-minute" : "1-hour"} candle just closed at ${clockText(t)} UTC. Which trading session are you in now?`,
      facts: [
        { label: "Candle", value: fifteen ? "15 minutes" : "1 hour" },
        { label: "Closed at", value: dateText(t) },
      ],
      options: SESSIONS.map(x => ({
        id: x.id,
        label: x.label,
        why: x.id === s.id ? undefined : `${x.label} runs ${hh(x.from)}–${hh(x.to)} UTC.`,
      })),
      answer: s.id,
      explain: `${s.label} runs ${hh(s.from)}–${hh(s.to)} UTC, so ${clockText(t)} is in it.${boundary} ${SESSION_NOTES[s.id]}`,
    };
  },
};

export const GAMES: GameDef[] = [
  riskDollars,
  positionSize,
  stopDistance,
  rewardToRisk,
  buyOrSellPlan,
  resultInR,
  breakevenWinRate,
  rToDollars,
  dayLimit,
  sessionName,
];
